import yup from 'utils/yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useState, useEffect } from 'react';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { AgentCompanyListingsQuery } from 'interfaceAdapter/queries/AgentCompanyListings';
import { AgentRequestMeetingRepository } from 'interfaceAdapter/repositories/AgentRequestMeeting';
import { BuyerRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/BuyerRegistrationProfileInfo';
import { IPublicBuyer } from 'domain/entities/Buyer/Buyer';
import { IAreaOption } from 'domain/entities/Buyer/Buyer';
import { useModal } from 'ui/components/screens/modals/useModal';
import { useParams, useNavigate } from 'react-router-dom';
import { agentUserContext } from 'application/contexts/useAgentUser';
import { useLoading } from 'application/contexts/useLoading';

const schema = yup
    .object({
        message: yup.string().max(1000),
        premium_offer: yup.boolean().required(),
    })
    .required();

type RequestMeetingForm = yup.InferType<typeof schema>;

export const useBuyerDetail = () => {
    const diContainer = useContext(diContainerContext);
    const agentCompanyListingsQuery = diContainer.resolve(AgentCompanyListingsQuery);
    const agentRequestMeetingRepository = diContainer.resolve(AgentRequestMeetingRepository);
    const buyerRegistrationProfileInfoQuery = diContainer.resolve(BuyerRegistrationProfileInfoQuery);
    const { loading, setLoading } = useLoading();
    const [buyer, setBuyer] = useState<IPublicBuyer>();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [areaChoices, setAreaChoices] = useState<IAreaOption[]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<string[][]>([]);
    const [profitLevelChoices, setProfitLevelChoices] = useState<string[][]>([]);
    const [netAssetsLevelChoices, setNetAssetsLevelChoices] = useState<string[][]>([]);
    const [hasAcquisitionExperienceChoices, setHasAcquisitionExperienceChoices] = useState<string[][]>([]);
    const { user } = useContext(agentUserContext);
    const params = useParams();
    const sendRequestModal = useModal();
    const needProfileModal = useModal();
    const navigate = useNavigate();

    const {
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
    } = useForm<RequestMeetingForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            message: '',
            premium_offer: false,
        },
    });

    useEffect(() => {
        if (params.buyer_id === undefined) {
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const resBuyers = await agentCompanyListingsQuery.fetchBuyers({});
                const targetBuyer = resBuyers.find((b: any) => b.id === Number(params.buyer_id));
                if (targetBuyer) {
                    setBuyer(targetBuyer as IPublicBuyer);
                }
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const fetchChoices = async () => {
            try {
                const data = await buyerRegistrationProfileInfoQuery.fetch();
                setAreaChoices(data.area_choices);
                setEmployeeChoices(data.employee_choices);
                setRevenueChoices(data.revenue_choices);
                setIndustryChoices(data.industry_choices);
                setProfitLevelChoices(data.profit_level_choices);
                setNetAssetsLevelChoices(data.net_assets_level_choices);
                setHasAcquisitionExperienceChoices(data.has_acquisition_experience_choices);
            } catch (error) {
                console.error('Failed to fetch choices:', error);
            }
        };
        fetchChoices();
    }, []);

    const submit = handleSubmit(async (data) => {
        try {
            setLoading(true);
            await agentRequestMeetingRepository.postToBuyer(
                data.message ?? '',
                data.premium_offer,
                Number(params.buyer_id),
            );
            sendRequestModal.closeModal();
            setShowSnackbar(true);
            setTimeout(() => {
                setShowSnackbar(false);
                navigate('/agent/offer/');
            }, 2000);
        } catch (e) {
            throw e;
        } finally {
            setLoading(false);
        }
    });

    return {
        user,
        loading,
        buyer,
        setValue,
        submit,
        watch,
        sendRequestModal,
        errors,
        showSnackbar,
        needProfileModal,
        setShowSnackbar,
        areaChoices,
        employeeChoices,
        revenueChoices,
        industryChoices,
        profitLevelChoices,
        netAssetsLevelChoices,
        hasAcquisitionExperienceChoices,
    };
};
